/**
 * What is on sale and for how much, from the live pricing feed.
 *
 * The homepage and the products grid both quote prices and both need to know
 * whether a title can be bought. They used to read the prices hardcoded in
 * data/games.tsx and never read the sales switches, so with Roblox sales off
 * both still offered it at "from €10 a week", while its own page -- which does
 * read the feed -- said purchases were disabled. One copy, used by both.
 *
 * Until the feed answers, or if it fails, the hardcoded prices stand in.
 */
import { useEffect, useState } from "react";
import type { Game, PlanType } from "../types/games";
import { entryPrice, formatPrice, savingPercent } from "../utils/plans";
import { salesKey } from "../utils/salesKey";
import { getPricingJson } from "../utils/pricingFeed";

type FeedPlan = { eur?: number; hasDiscount?: boolean; originalEur?: number };
export type Feed = {
  prices: Record<string, Partial<Record<PlanType, FeedPlan>>>;
  sales: Record<string, boolean>;
};

/* The raw response is shared with the product page and the click tracker
   (utils/pricingFeed), so a first visit asks for it once. */
function loadFeed(): Promise<Feed | null> {
  return getPricingJson().then((data) => {
    if (!data) return null;
    const prices: Feed["prices"] = {};
    for (const [key, value] of Object.entries(data.games ?? {})) {
      prices[key] = (value as { pricing?: Feed["prices"][string] })?.pricing ?? {};
    }
    return { prices, sales: (data.settings?.productSalesEnabled as Record<string, boolean>) ?? {} };
  });
}

/** The feed, and whether it has answered yet (successfully or not). */
export function useLiveCatalog() {
  const [feed, setFeed] = useState<Feed | null>(null);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadFeed().then((f) => {
      if (cancelled) return;
      setFeed(f);
      setSettled(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { feed, settled };
}

/** A title is only buyable once it is out of development. */
export const isBuyable = (game: Game) =>
  game.status !== "coming-soon" &&
  game.status !== "in-development" &&
  game.status !== "partial-development";

/** Buyable, and not switched off in admin. */
export const salesOpen = (game: Game, feed: Feed | null) =>
  isBuyable(game) && (feed ? feed.sales[salesKey(game.id)] !== false : true);

export const PLAN_ORDER: PlanType[] = ["weekly", "monthly", "quarterly", "biannual", "lifetime"];
export const PLAN_UNIT: Record<PlanType, string> = {
  weekly: "week",
  monthly: "month",
  quarterly: "quarter",
  biannual: "6 months",
  lifetime: "lifetime",
};

export type PlanPrice = { price: string; was?: string };

/** Every plan a title sells, from the feed when it has the title. */
export function planPrices(game: Game, feed: Feed | null): Partial<Record<PlanType, PlanPrice>> {
  const live = feed?.prices[game.id] ?? feed?.prices[salesKey(game.id)];
  const out: Partial<Record<PlanType, PlanPrice>> = {};
  for (const plan of PLAN_ORDER) {
    if (live) {
      /* The feed is the whole truth for a title it knows: a plan it does not
         list is a plan not on sale, so the hardcoded one must not fill in. */
      const p = live[plan];
      if (p?.eur && p.eur > 0) {
        out[plan] = {
          price: formatPrice(p.eur, "€"),
          was: p.hasDiscount && p.originalEur && p.originalEur > p.eur ? formatPrice(p.originalEur, "€") : undefined,
        };
      }
    } else if (game.pricing[plan]) {
      out[plan] = { price: game.pricing[plan]! };
    }
  }
  return out;
}

/** The entry plan, and the next one up with what it saves per week. */
export function priceSummary(prices: Partial<Record<PlanType, PlanPrice>>) {
  const flat = Object.fromEntries(Object.entries(prices).map(([k, v]) => [k, v!.price]));
  const entry = entryPrice(flat);
  if (!entry) return null;
  const nextPlan = PLAN_ORDER.slice(PLAN_ORDER.indexOf(entry.plan) + 1).find((p) => prices[p]);
  const next = nextPlan
    ? {
        plan: nextPlan,
        price: prices[nextPlan]!.price,
        unit: PLAN_UNIT[nextPlan],
        saving: savingPercent(prices[nextPlan]!.price, nextPlan, entry.display, entry.plan),
      }
    : null;
  return { entry: { ...entry, was: prices[entry.plan]?.was }, next };
}
